import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import QuoteForm from "../components/QuoteForm";
import { Reveal } from "../components/motion";
import { ADDRESS, ORAI_BRANCH, PHONE_DISPLAY, PHONE_TEL, PHONE, EMAIL, EMAIL_MAILTO, MAPS_EMBED } from "../lib/site";

const cards = [
  { icon: MapPin, label: "Head Office", value: ADDRESS, href: null, cta: null, testid: "contact-card-office" },
  { icon: MapPin, label: "Branch Office (Orai)", value: ORAI_BRANCH.address, href: null, cta: null, testid: "contact-card-branch" },
  { icon: Phone, label: "Phone", value: PHONE_DISPLAY, href: PHONE_TEL, cta: `Call ${PHONE}`, testid: "contact-card-phone" },
  { icon: Mail, label: "Email", value: EMAIL, href: EMAIL_MAILTO, cta: "Email Us", testid: "contact-card-email" },
];

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="Let's Talk About Your"
        highlight="Insurance Needs."
        subtitle="Call, email or send us an enquiry — a real insurance professional will get back to you."
      />

      <section className="bg-ice py-24" data-testid="contact-details-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift" data-testid={c.testid}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-skycyan/15 to-mint/15">
                    <c.icon className="h-6 w-6 text-skycyan-600" strokeWidth={1.9} />
                  </span>
                  <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-skycyan-600">{c.label}</p>
                  <p className="mt-2 text-base font-semibold leading-relaxed text-navy">{c.value}</p>
                  {c.href && (
                    <a href={c.href} className="mt-auto inline-flex w-fit items-center gap-1.5 pt-6 text-sm font-bold text-mint-600" data-testid={`${c.testid}-cta`}>
                      {c.cta} <ArrowRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid items-start gap-14 lg:grid-cols-[1fr_1.2fr]">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Enquire</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="contact-form-heading">
                Let's Find the Right Protection for You
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-charcoal/80">
                Fill in the form and our team will reach out with guidance on suitable insurance options.
              </p>
              <div className="mt-8 overflow-hidden rounded-[2rem] border border-navy/10 shadow-soft" data-testid="contact-page-map">
                <iframe
                  title="PocketBima office — N-303, Stellar Jeevan, Greater Noida West"
                  src={MAPS_EMBED}
                  className="h-[300px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-[2rem] border border-navy/10 bg-white p-7 shadow-soft sm:p-10">
                <QuoteForm />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-[2rem] border border-navy/10 bg-white p-8 shadow-soft sm:p-10" data-testid="contact-next-steps">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
                What Happens After You Enquire
              </h2>
              <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { n: "01", t: "You share what you want to protect — your vehicle, health, family, trip or business." },
                  { n: "02", t: "Our team reviews your details and identifies suitable insurance options from insurers." },
                  { n: "03", t: "We call or WhatsApp you back to explain coverage, premium factors and important terms in simple language." },
                  { n: "04", t: "You decide at your own pace — no pressure, and no charge for guidance, ever." },
                ].map((s) => (
                  <div key={s.n} data-testid={`contact-step-${s.n}`}>
                    <span className="font-display text-2xl font-extrabold text-mint">{s.n}</span>
                    <p className="mt-2 text-sm leading-relaxed text-charcoal/80">{s.t}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 max-w-3xl text-sm leading-relaxed text-charcoal/80">
                Pocket Bima Insurance Service serves customers across Greater Noida West, Noida,
                Delhi NCR and the Orai region of Uttar Pradesh. Whether you walk into our head
                office at Stellar Jeevan, visit the Orai branch near Hotel Anandi Inn, call
                +91 95980 63001, or send a WhatsApp message, you will always reach a real person.
                Enquiries and guidance are completely free — you only ever pay the insurer's
                premium when you choose a policy.
              </p>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-charcoal/80">
                We can help you with car insurance, bike insurance, taxi and goods-carrying
                commercial vehicle insurance, third party insurance, individual and family health
                insurance, group health and group personal accident cover, life and term
                insurance, group term plans, fire insurance, workmen compensation, marine and
                single transit insurance, and travel insurance for domestic and international
                journeys. If your need is not on this list, ask us anyway — we will point you in
                the right direction.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
